// ============================================================================
// The suite switcher (2026-10-06; docs/suites.md "Day to day"). One switcher in
// two shapes:
// - On a phone, a sheet of cards. Each card is the suite as it looks: its
//   wallpaper in its colour, its icon and name, and the first apps of its dock.
//   Holding the plus opens it, and sliding onto a card and letting go switches
//   in one gesture (`useSuiteAim`, fed by the plus in the Shell).
// - On a desktop, a menu under the sidebar's title, run from the keyboard
//   (arrows, Enter, 1–9 and 0, Escape), with ⌃1–⌃9 from anywhere on a Mac
//   (`pickSuiteByDigit`, from hooks/useKeyboard).
// A switch keeps the page when the other suite has it (suites/switch.ts) and
// says where you are now with a short flash (`SuiteFlash`).
// ============================================================================
import { useEffect, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { create } from 'zustand'
import { PrimeMark } from '@/components/PrimeMark'
import { Check, LayoutGrid, Pencil, Plus } from 'lucide-react'
import { useSettings, useWorkspaces, useResolvedAppearance } from '@/db/hooks'
import type { WorkspaceResource } from '@/kinds/workspace/apply'
import { WorkspaceForm } from '@/kinds/workspace/components'
import { ALL } from '@/kinds/workspace/schema'
import { accentVarsFor, tokens, type Hue } from '@/lib/colour'
import { paletteFor } from '@/lib/look'
import { tick } from '@/lib/haptics'
import { Icon } from '@/lib/icons'
import type { NavItem } from '@/lib/nav'
import { appsFor } from '@/lib/useNav'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Backdrop } from '@/os/Backdrop'
import { useHold } from '@/os/AppIcon'
import type { PhoneSettings } from '@/os/layout'
import { useAccess } from '@/state/access'
import { useScope } from '@/state/scope'
import { suiteOf } from '@/suites'

/** Whether a suite is called what it was installed as ("Life" from Life), so its subtitle would only repeat it. */
const sameName = (w: WorkspaceResource) => w.spec.name.trim().toLowerCase() === suiteOf(w).title.toLowerCase()
import { landingAfterSwitch, orderSuites, suiteDigit, suiteForDigit, suitePreview } from '@/suites/switch'
import { Sheet, cx } from './ui'

/** The Shell's phone/desktop line (its `md:` breakpoint). */
const WIDE = '(min-width: 768px)'

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)

/** Which suite a finger sliding up from the plus is over: the hold-and-slide switch. */
export const useSuiteAim = create<{ aim: string | null; setAim: (aim: string | null) => void }>((set) => ({
  aim: null,
  setAim: (aim) => set({ aim }),
}))

/** The last switch, for the flash that says where you are now. */
const useFlash = create<{
  shown: { key: number; icon?: string; name: string; colour?: string } | null
  show: (s: { icon?: string; name: string; colour?: string }) => void
  clear: () => void
}>((set) => ({
  shown: null,
  show: (s) => set({ shown: { ...s, key: Date.now() } }),
  clear: () => set({ shown: null }),
}))

// the switcher is always mounted in the Shell and keeps this current, so a
// shortcut pressed with the switcher shut picks from the same order it shows
let ordered: WorkspaceResource[] = []

/**
 * Switch the device to a suite and go where suites/switch.ts says. Nothing
 * happens for the suite you are in.
 */
export const pickSuite = (slug: string, go: (to: string) => void, fromLauncher = false) => {
  const scope = useScope.getState()
  if (scope.ws === slug) return
  const w = ordered.find((x) => x.spec.slug === slug)
  if (slug !== ALL && !w) return
  scope.setWs(slug)
  const phone = typeof window !== 'undefined' && !window.matchMedia(WIDE).matches
  const apps = w ? appsFor({ nav: w.spec.nav, packs: w.spec.packs, suite: w.spec.suite }) : null
  go(landingAfterSwitch(window.location.pathname, phone, apps, fromLauncher))
  tick(8)
  useFlash
    .getState()
    .show(w ? { icon: w.spec.icon, name: w.spec.name, colour: w.spec.colour } : { name: 'Everything' })
}

/** ⌃1–⌃9 and ⌃0 from the keyboard; false when the digit names no suite. */
export const pickSuiteByDigit = (digit: string, go: (to: string) => void): boolean => {
  const slug = suiteForDigit(digit, ordered)
  if (!slug) return false
  pickSuite(slug, go)
  return true
}

const shortcut = (digit: string | undefined) => (digit ? (isMac() ? `⌃${digit}` : digit) : undefined)

/** Who opens the switcher: the plus (held), the sidebar's title, `g w`, the launcher. */
export const SuiteSwitcher = ({
  open,
  onClose,
  onInstall,
}: {
  open: boolean
  onClose: () => void
  onInstall: () => void
}) => {
  const workspaces = useWorkspaces()
  const live = orderSuites(workspaces)
  const wide = useMediaQuery(WIDE)
  const navigate = useNavigate()
  const [editing, setEditing] = useState<WorkspaceResource | null>(null)
  useEffect(() => {
    ordered = live
  })
  // done editing (saved or not) is done with the switcher too: back to the app
  if (editing)
    return (
      <WorkspaceForm
        resource={editing}
        onDone={() => {
          setEditing(null)
          onClose()
        }}
      />
    )
  if (!open) return null
  const choose = (slug: string) => {
    onClose()
    pickSuite(slug, (to) => void navigate({ to }))
  }
  const install = () => {
    onClose()
    onInstall()
  }
  const props = { live, choose, install, onClose, onEdit: setEditing }
  return wide ? <SuiteMenu {...props} /> : <SuiteSheet {...props} />
}

interface ShapeProps {
  live: WorkspaceResource[]
  choose: (slug: string) => void
  install: () => void
  onClose: () => void
  onEdit: (w: WorkspaceResource) => void
}

// ---- the phone: a sheet of cards ---------------------------------------------------

/** The phone's way to Prime's own thread from the suites sheet; not for a member, whose Prime is /ask. */
export const PrimeShortcut = ({ onClose }: { onClose: () => void }) => {
  const member = useAccess((s) => s.member)
  if (member) return null
  return (
    <Link
      to="/agents"
      onClick={onClose}
      className="mb-2.5 w-full flex items-center gap-3 rounded-[var(--radius-card)] border border-line bg-raised px-3.5 min-h-14 text-left"
      data-testid="suites-prime"
    >
      <PrimeMark size={20} className="w-7" aria-hidden />
      <span className="flex-1 min-w-0">
        <span className="block font-semibold">Prime</span>
        <span className="block text-xs text-muted">Open its thread</span>
      </span>
    </Link>
  )
}

const SuiteSheet = ({ live, choose, install, onClose, onEdit }: ShapeProps) => {
  const scope = useScope((s) => s.ws)
  const aim = useSuiteAim((s) => s.aim)
  return (
    <Sheet open onClose={onClose} title="Suites">
      <PrimeShortcut onClose={onClose} />
      <div className="grid grid-cols-2 gap-2.5" data-testid="workspace-list">
        {live.map((w) => (
          <SuiteCard
            key={w.id}
            w={w}
            current={w.spec.slug === scope}
            aimed={aim === w.spec.slug}
            onPick={() => choose(w.spec.slug)}
            onEdit={() => onEdit(w)}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => choose(ALL)}
        data-suite={ALL}
        className={cx(
          'mt-2.5 w-full flex items-center gap-3 rounded-[var(--radius-card)] border px-3.5 min-h-14 text-left transition-transform',
          scope === ALL ? 'border-accent bg-accent-soft' : 'border-line bg-raised',
          aim === ALL && 'ring-2 ring-accent scale-[1.02]',
        )}
        data-testid="workspace-all"
      >
        <span className="text-xl w-7 text-center">✦</span>
        <span className="flex-1 min-w-0">
          <span className="block font-semibold">Everything</span>
          <span className="block text-xs text-muted">One Today across every suite</span>
        </span>
        {scope === ALL && <Check size={18} className="text-accent" aria-hidden />}
      </button>
      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          onClick={install}
          className="flex-1 flex items-center justify-center gap-1.5 rounded-2xl border border-line bg-raised min-h-11 text-sm font-medium"
          data-testid="workspace-new"
        >
          <Plus size={16} aria-hidden />
          Install a suite
        </button>
        <Link
          to="/settings/$section"
          params={{ section: 'workspaces' }}
          onClick={onClose}
          className="flex-1 flex items-center justify-center gap-1.5 rounded-2xl border border-line bg-raised min-h-11 text-sm font-medium"
          data-testid="suites-manage"
        >
          <LayoutGrid size={16} aria-hidden />
          Manage suites
        </Link>
      </div>
      <p className="text-xs text-faint mt-3 px-1">Hold + and slide onto a suite to switch in one go.</p>
    </Sheet>
  )
}

/** One suite as it looks: its wallpaper in its colour, its name, the apps it leads with. */
const SuiteCard = ({
  w,
  current,
  aimed,
  onPick,
  onEdit,
}: {
  w: WorkspaceResource
  current: boolean
  aimed: boolean
  onPick: () => void
  onEdit: () => void
}) => {
  const settings = useSettings() as PhoneSettings
  const resolved = useResolvedAppearance()
  const hold = useHold(onEdit)
  const hue = w.spec.colour as Hue
  const apps = appsFor({ nav: w.spec.nav, packs: w.spec.packs, suite: w.spec.suite })
  const byId = new Map<string, NavItem>(apps.map((a) => [a.key, a]))
  const preview = suitePreview(settings ?? {}, w.spec.slug, (id) => byId.has(id))
  // the suite's own accent on the card, so its wallpaper and edge are its colour, not this one's
  const vars = accentVarsFor(paletteFor(w.spec.theme, w.spec.palette), hue, resolved === 'day')
  // a div holds the card's two buttons (the card itself, and Edit), since a button cannot hold another
  return (
    <div
      data-suite={w.spec.slug}
      className={cx(
        'relative h-36 overflow-hidden rounded-[var(--radius-card)] border bg-ink hold-safe transition-transform duration-150',
        current ? 'border-accent' : 'border-line',
        aimed && 'ring-2 ring-accent scale-[1.03]',
      )}
      style={vars as React.CSSProperties}
    >
      <Backdrop kind={preview.wallpaper} />
      <span
        className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent pointer-events-none"
        aria-hidden
      />
      <button
        type="button"
        {...hold.handlers}
        onClick={() => {
          if (!hold.held()) onPick()
        }}
        aria-label={`${w.spec.name}${current ? ', current suite' : ''}`}
        aria-current={current || undefined}
        className="absolute inset-0 text-left"
        data-testid={`workspace-${w.spec.slug}`}
      >
        <span className="absolute top-2.5 left-2.5 h-9 w-9 rounded-xl bg-panel/90 border border-line flex items-center justify-center text-lg">
          <Icon value={w.spec.icon} size={18} fallback="◉" />
        </span>
        {current && (
          <span className="absolute top-2.5 right-2.5 h-6 w-6 rounded-full bg-accent text-on-accent flex items-center justify-center">
            <Check size={14} strokeWidth={2.6} aria-hidden />
          </span>
        )}
        <span className="absolute inset-x-2.5 bottom-2.5 flex flex-col gap-1.5">
          <span className="flex gap-1" aria-hidden>
            {preview.apps.map((id) => {
              const a = byId.get(id)!
              return (
                <span
                  key={id}
                  className="h-6 w-6 rounded-md bg-panel/85 border border-line text-fg-2 flex items-center justify-center text-[11px]"
                >
                  {a.Icon ? <a.Icon size={13} /> : a.icon}
                </span>
              )
            })}
          </span>
          <span className="min-w-0 pr-8">
            <span className="block font-semibold truncate text-fg">{w.spec.name}</span>
            {/* what it was installed as, only when its name does not say so already */}
            {sameName(w) || <span className="block text-[11px] text-muted truncate">{suiteOf(w).title}</span>}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onEdit}
        aria-label={`Edit ${w.spec.name}`}
        className="absolute bottom-1.5 right-1.5 h-9 w-9 rounded-xl text-muted flex items-center justify-center"
      >
        <Pencil size={14} aria-hidden />
      </button>
    </div>
  )
}

// ---- the desktop: a menu under the title -------------------------------------------

/** The visible element the menu hangs from: the sidebar's title, or its icon when folded. */
const anchorRect = (): DOMRect | null => {
  for (const el of document.querySelectorAll<HTMLElement>('[data-suite-anchor]')) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) return r
  }
  return null
}

const SuiteMenu = ({ live, choose, install, onClose, onEdit }: ShapeProps) => {
  const scope = useScope((s) => s.ws)
  const navigate = useNavigate()
  const rows = [...live.map((w) => w.spec.slug), ALL]
  const [cursor, setCursor] = useState(() => Math.max(0, rows.indexOf(scope)))
  // read once as the menu opens; opened with `g w` and no title showing, near the top like the palette
  const [at] = useState(() => {
    const r = anchorRect()
    return r
      ? { top: r.bottom + 6, left: Math.max(8, r.left) }
      : { top: 72, left: window.innerWidth / 2 - 160 }
  })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.altKey) return
      const digit = /^[0-9]$/.test(e.key) ? e.key : undefined
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowDown') setCursor((c) => (c + 1) % rows.length)
      else if (e.key === 'ArrowUp') setCursor((c) => (c - 1 + rows.length) % rows.length)
      else if (e.key === 'Enter') choose(rows[cursor]!)
      else if (digit) {
        const slug = suiteForDigit(digit, live)
        if (!slug) return
        choose(slug)
      } else return
      e.preventDefault()
      e.stopPropagation()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })
  const row = (slug: string, i: number, body: React.ReactNode, w?: WorkspaceResource) => {
    const current = slug === scope
    return (
      <li key={slug} className="group relative">
        <button
          type="button"
          onClick={() => choose(slug)}
          onMouseEnter={() => setCursor(i)}
          className={cx(
            'w-full flex items-center gap-2.5 rounded-lg px-2.5 min-h-10 text-left text-sm',
            i === cursor && 'bg-raised',
          )}
          aria-current={current || undefined}
          data-testid={slug === ALL ? 'workspace-all' : `workspace-${slug}`}
        >
          {body}
          <span className="w-4 flex justify-center">
            {current && <Check size={15} className="text-accent" aria-hidden />}
          </span>
          <kbd className="min-w-7 text-right text-[11px] text-faint font-sans">
            {shortcut(suiteDigit(slug, live))}
          </kbd>
        </button>
        {w && (
          <button
            type="button"
            onClick={() => onEdit(w)}
            aria-label={`Edit ${w.spec.name}`}
            title={`Edit ${w.spec.name}`}
            className="absolute right-[4.25rem] top-1/2 -translate-y-1/2 h-7 w-7 rounded-md text-faint hover:text-fg hover:bg-panel flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          >
            <Pencil size={13} aria-hidden />
          </button>
        )}
      </li>
    )
  }
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-label="Suites"
        className="fixed z-50 w-80 rounded-[var(--radius-card)] border border-line bg-panel p-1.5 shadow-[var(--shadow-pop)] fade-in"
        style={{ top: at.top, left: at.left }}
      >
        <ul className="flex flex-col" data-testid="workspace-list">
          {live.map((w, i) => {
            const t = tokens(w.spec.colour as Hue)
            return row(
              w.spec.slug,
              i,
              <>
                <span
                  className="h-7 w-7 shrink-0 rounded-md flex items-center justify-center text-base"
                  style={{ background: `color-mix(in oklab, ${t.accent} 18%, transparent)` }}
                >
                  <Icon value={w.spec.icon} size={15} fallback="◉" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium truncate">{w.spec.name}</span>
                  {sameName(w) || (
                    <span className="block text-[11px] text-faint truncate">{suiteOf(w).title}</span>
                  )}
                </span>
              </>,
              w,
            )
          })}
          <li className="my-1 border-t border-line" aria-hidden />
          {row(
            ALL,
            live.length,
            <>
              <span className="h-7 w-7 shrink-0 rounded-md bg-raised flex items-center justify-center">
                ✦
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-medium">Everything</span>
                <span className="block text-[11px] text-faint">One Today across every suite</span>
              </span>
            </>,
          )}
        </ul>
        <div className="mt-1 pt-1 border-t border-line flex flex-col">
          <button
            type="button"
            onClick={install}
            className="flex items-center gap-2.5 rounded-lg px-2.5 min-h-9 text-sm text-muted hover:bg-raised hover:text-fg"
            data-testid="workspace-new"
          >
            <Plus size={15} aria-hidden />
            Install a suite
          </button>
          <button
            type="button"
            onClick={() => {
              onClose()
              void navigate({ to: '/settings/$section', params: { section: 'workspaces' } })
            }}
            className="flex items-center gap-2.5 rounded-lg px-2.5 min-h-9 text-sm text-muted hover:bg-raised hover:text-fg"
            data-testid="suites-manage"
          >
            <LayoutGrid size={15} aria-hidden />
            Manage suites
          </button>
        </div>
      </div>
    </>
  )
}

// ---- after a switch ----------------------------------------------------------------

/** A short line at the top saying which suite you are in now. */
export const SuiteFlash = () => {
  const shown = useFlash((s) => s.shown)
  const clear = useFlash((s) => s.clear)
  if (!shown) return null
  const t = tokens(shown.colour as Hue | undefined)
  return (
    <div
      key={shown.key}
      className="suite-flash fixed left-1/2 z-50 pointer-events-none flex items-center gap-2 rounded-full border border-line bg-panel px-3.5 py-1.5 text-sm font-medium shadow-[var(--shadow-pop)]"
      style={{ top: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      onAnimationEnd={clear}
      role="status"
      data-testid="suite-flash"
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ background: shown.colour ? t.accent : 'var(--color-fg)' }}
      />
      {shown.icon ? <Icon value={shown.icon} size={15} /> : <span>✦</span>}
      {shown.name}
    </div>
  )
}

/** The launcher's row of suites: one tap switches, and the Apps screen shows that suite's. */
export const SuiteStrip = () => {
  const workspaces = useWorkspaces()
  const live = orderSuites(workspaces)
  const scope = useScope((s) => s.ws)
  const navigate = useNavigate()
  if (live.length < 2) return null
  const chip = (slug: string, label: string, icon: React.ReactNode) => (
    <button
      key={slug}
      type="button"
      onClick={() => pickSuite(slug, (to) => void navigate({ to }), true)}
      aria-current={slug === scope || undefined}
      className={cx(
        'shrink-0 flex items-center gap-1.5 rounded-full border px-3 min-h-9 text-sm',
        slug === scope
          ? 'bg-accent text-on-accent border-transparent font-semibold'
          : 'bg-raised/80 border-line text-fg-2',
      )}
      data-testid={`strip-${slug}`}
    >
      {icon}
      {label}
    </button>
  )
  return (
    <div className="flex gap-1.5 overflow-x-auto -mx-4 px-4 mt-3 pb-0.5" data-testid="suite-strip">
      {live.map((w) => chip(w.spec.slug, w.spec.name, <Icon value={w.spec.icon} size={15} fallback="◉" />))}
      {chip(ALL, 'Everything', <span>✦</span>)}
    </div>
  )
}
